import type { Variants } from 'framer-motion';
import { AnimatePresence, motion } from 'framer-motion';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import animationConfig from '@/../config/animation.json';
import statusConfig from '@/../config/status.json';
import { useAppContext } from '@/app/providers/AppContextProvider';
import CoverageWarningBanner from '@/features/Quote/components/CoverageWarningBanner';
import MkbBundleWarningBanner from '@/features/Quote/components/MkbBundleWarningBanner';
import { detectUnconventionalConfig } from '@/features/Quote/services/coverageRules';
import { cn } from '@/lib/utils';
import {
	getSavingPercentages,
	isUnavailable,
	sortQuotesByInsurer,
	sortQuotesByPremiumPercentage
} from '@/services/utils';
import type { CoverageValues, Quote, SavingPercentages, SettingType } from '@/types/app';
import Item from './Item';
import Heading from './items/Heading';
import ProgressBar from './items/Progressbar';
import Settings from './Settings';

interface Customer {
	company_name?: string;
	coc?: string;
	sector_name?: string;
	address?: string;
	zip_code?: string;
	city?: string;
}

interface ItemsProps {
	/** Restart app-state polling — passed to Settings so a recalculation re-fetches. */
	onRecalculate: () => void;
}

const Items = ({ onRecalculate }: ItemsProps) => {
	const [savingPercentages, setSavingPercentages] = useState<SavingPercentages>({ max: 0, min: 0 });
	const [sortedQuotes, setSortedQuotes] = useState<Quote[]>([]);
	const [isEditingSettings, setIsEditingSettings] = useState(false);
	const [currentSort, setCurrentSort] = useState<string>('premium');
	const openSettingFunctionRef = useRef<
		((settingType: SettingType, fromQuoteSelection?: boolean) => void) | null
	>(null);
	const { appData, isLoading } = useAppContext();
	const { id } = useParams<{ id: string }>();
	const quotes = useMemo(
		() => appData?.quotes?.sick_leave_short_term || [],
		[appData?.quotes?.sick_leave_short_term]
	);

	const loading =
		isLoading || !appData?.status || !statusConfig.groups.isComplete.includes(appData.status);

	// Get current coverage values
	const currentCoverage = useMemo<CoverageValues | null>(() => {
		const coverage = appData?.premium_variables?.coverage?.sick_leave_short_term;
		if (!coverage) return null;

		return {
			waiting_period: coverage.waiting_period ?? 30,
			employer_costs: coverage.employer_costs ?? 0,
			coverage_percentage_first_year_first_half:
				coverage.coverage_percentage_first_year_first_half || 100,
			coverage_percentage_first_year_second_half:
				coverage.coverage_percentage_first_year_second_half || 100,
			coverage_percentage_second_year_first_half:
				coverage.coverage_percentage_second_year_first_half || 100,
			coverage_percentage_second_year_second_half:
				coverage.coverage_percentage_second_year_second_half || 100
		};
	}, [appData?.premium_variables?.coverage?.sick_leave_short_term]);

	// Detect unconventional configuration
	const unconventionalDetection = useMemo(() => {
		if (!currentCoverage)
			return { hasIssues: false, triggeredRules: [], hasDowngrade: false, suggestedConfig: null };
		return detectUnconventionalConfig(currentCoverage);
	}, [currentCoverage]);

	const hasMkbBundleWarning = useMemo(
		() => (appData?.policy_warnings ?? []).includes('SME_ABSENCE_RELIEF_BUNDLE'),
		[appData?.policy_warnings]
	);

	// Callback to capture the openSetting function from Settings
	const handleOpenSettingRef = useCallback(
		(openSettingFn: (settingType: SettingType, fromQuoteSelection?: boolean) => void) => {
			openSettingFunctionRef.current = openSettingFn;
		},
		[]
	);

	// Handler to open coverage settings from warning banner
	const handleAdjustCoverage = useCallback(() => {
		if (openSettingFunctionRef.current) {
			openSettingFunctionRef.current('coverage', false);
		}
	}, []);

	const sortQuotes = useCallback((data: Quote[], sortType: string): Quote[] => {
		switch (sortType) {
			case 'premium':
				return sortQuotesByPremiumPercentage(data);
			case 'insurer':
				return sortQuotesByInsurer(data);
			default:
				return sortQuotesByPremiumPercentage(data);
		}
	}, []);

	const handleSortChange = (newSortType: string) => {
		setCurrentSort(newSortType);
		if (quotes.length > 0) {
			setSortedQuotes(sortQuotes(quotes, newSortType));
		}
	};

	// Calculate which quote has the best savings (highest saving_amount)
	const bestOfferQuoteId = useMemo(() => {
		if (quotes.length === 0) return null;
		const availableQuotes = quotes.filter(
			(quote: Quote) =>
				!isUnavailable(quote) && quote.saving_amount != null && quote.saving_amount > 0
		);
		if (availableQuotes.length === 0) return null;

		const bestQuote = availableQuotes.reduce((best: Quote, current: Quote) =>
			(current.saving_amount ?? 0) > (best.saving_amount ?? 0) ? current : best
		);
		return bestQuote.quote_id;
	}, [quotes]);

	useEffect(() => {
		if (quotes.length > 0) {
			setSavingPercentages(getSavingPercentages(quotes));
			setSortedQuotes(sortQuotes(quotes, currentSort));
		}
	}, [quotes, currentSort, sortQuotes]);

	return (
		<div>
			<Heading
				customer={appData?.company_info as Customer | undefined}
				savingPercentages={savingPercentages}
				loading={loading}
				status={appData?.status}
				expectedNumQuotes={appData?.expected_num_quotes || 0}
				quotesReady={quotes.length}
				onSortChange={handleSortChange}
				currentSort={currentSort}
			/>

			<hr className="my-6 border-border" />

			<Settings
				loading={loading}
				setIsEditingSettings={setIsEditingSettings}
				onOpenSetting={handleOpenSettingRef}
				onRecalculate={onRecalculate}
			/>

			{/* MKB Verzuim-Ontzorgverzekering bundle notice — rendered in the
			    same shared warning slot as the coverage banner so the page only
			    has one place to look for above-the-quotes callouts. */}
			{hasMkbBundleWarning && (
				<div className="mb-4">
					<MkbBundleWarningBanner isPolling={loading} />
				</div>
			)}

			{/* Show warning banner if unconventional configuration detected */}
			{currentCoverage && unconventionalDetection.hasIssues && (
				<div className="mb-4">
					<CoverageWarningBanner
						detection={unconventionalDetection}
						onAdjustCoverage={handleAdjustCoverage}
						isPolling={loading}
					/>
				</div>
			)}

			<ProgressBar loading={loading} />

			<div
				data-items-container
				className={cn(
					'pt-3 transition-all duration-200 overflow-visible',
					isEditingSettings && 'pointer-events-none opacity-50 grayscale'
				)}
			>
				<motion.div
					variants={animationConfig.container as Variants}
					initial="hidden"
					animate="show"
					className="overflow-visible"
				>
					<AnimatePresence>
						{sortedQuotes.map((quote) => (
							<motion.div
								key={quote.quote_id}
								variants={animationConfig.item as Variants}
								layout
								className="overflow-visible"
							>
								<Item
									formId={id || ''}
									quoteId={quote.quote_id}
									insurerSlug={quote.insurer}
									insurerName={quote.insurer_name}
									imageKey={quote.image_key}
									premiumRatePercentage={quote.premium_rate_percentage || 0}
									savingPercentage={quote.saving_percentage}
									savingAmount={quote.saving_amount}
									isBestOffer={quote.quote_id === bestOfferQuoteId}
									selected={false}
									complete={false}
									isPolling={loading}
									isUnavailable={isUnavailable(quote)}
									openSetting={openSettingFunctionRef.current || undefined}
								/>
							</motion.div>
						))}
					</AnimatePresence>
				</motion.div>
			</div>
		</div>
	);
};

export default Items;
